/**
 * Dynamic-date support for saved views (PTC-CRM-XCUT-03 AC2), and the fleet filter contract's token
 * set (§5.1, v2 §12.2).
 *
 * Several views filter on relative dates ("Past due", "Due this week", "This month"). A view's
 * `query` is a static `Record<string, string>`, so the relative boundary is encoded as a `{{token}}`
 * and resolved to a concrete ISO-8601 instant when the list is read — never when the view is built,
 * so a *Today* saved on the 19th still means today when opened on the 25th (C52).
 *
 * 🔑 **Two resolution contexts (contract §9.3), and SUP-001 was that only one existed.**
 * - **`interactive`** — a grid a person is reading. Calendar boundaries are the STAFF zone's,
 *   `America/New_York`, resolved through the IANA zone so DST is honoured (a fixed `-04:00` buckets
 *   correctly all summer and wrongly all winter). **The default**, because every grid in this app is
 *   interactive.
 * - **`saved`** — a context with no reader's day to honour (server-side evaluation). UTC.
 *
 * Until 2026-10-20 every token resolved in UTC regardless, so from 20:00 to midnight ET "Today"
 * meant tomorrow for every staff member — invisible on a UTC CI runner, live for every real user.
 *
 * Every `*End` token is EXCLUSIVE: the start of the first day after the period (half-open).
 * `{{dayAfterTomorrow}}` is a name, not arithmetic, on purpose (contract §12.2): the token set is a
 * lookup table, never a grammar.
 */

export type ViewQueryToken =
  | '{{now}}'
  | '{{today}}'
  | '{{tomorrow}}'
  | '{{yesterday}}'
  | '{{dayAfterTomorrow}}'
  | '{{weekStart}}'
  | '{{weekEnd}}'
  | '{{monthStart}}'
  | '{{monthEnd}}'
  | '{{quarterStart}}'
  | '{{quarterEnd}}'
  | '{{yearStart}}'
  | '{{yearEnd}}';

/** Where a token's calendar boundaries are drawn (contract §9.3). */
export type TokenContext = 'interactive' | 'saved';

/** The staff-interactive zone. An IANA name, never an offset. */
export const STAFF_ZONE = 'America/New_York';

const TOKEN_PATTERN = /^\{\{[a-zA-Z]+\}\}$/;

/** Exported for groupBy.ts (fleet filter contract v3 §13.3, M5): date-column grouping buckets
 * resolve in this SAME staff-interactive, IANA-zone-correct way, not a reimplementation. */
export interface CalendarDay {
  y: number;
  /** 1–12 */
  m: number;
  d: number;
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let f = partsCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    partsCache.set(zone, f);
  }
  return f;
}

/** The wall-clock fields of `instant` in `zone`. */
export function wallClock(zone: string, instant: number) {
  const parts = formatterFor(zone).formatToParts(new Date(instant));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
    hh: Number(get('hour')),
    mm: Number(get('minute')),
    ss: Number(get('second')),
    weekday: get('weekday'),
  };
}

/** Milliseconds `zone` is ahead of UTC at `instant` (negative west of Greenwich). */
function zoneOffsetMs(zone: string, instant: number): number {
  const w = wallClock(zone, instant);
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm, w.ss);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/** The instant at which `day` begins in `zone` — DST-correct. */
function startOf(zone: string, day: CalendarDay): Date {
  const guess = Date.UTC(day.y, day.m - 1, day.d);
  const first = guess - zoneOffsetMs(zone, guess);
  // Re-read the offset AT the candidate instant: on a DST-change day the guess's offset is the
  // other side of the change.
  const second = guess - zoneOffsetMs(zone, first);
  return new Date(second);
}

/** Calendar arithmetic, done on the calendar (never on instants, so DST cannot leak in). */
export function addDays(day: CalendarDay, n: number): CalendarDay {
  const t = new Date(Date.UTC(day.y, day.m - 1, day.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function addMonths(day: CalendarDay, n: number): CalendarDay {
  const t = new Date(Date.UTC(day.y, day.m - 1 + n, 1));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: 1 };
}

export const MONDAY_OFFSET: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Resolve a single token against `now`, or `null` if the token is not recognised. */
function resolveToken(token: string, now: Date, context: TokenContext): string | null {
  if (token === '{{now}}') return now.toISOString();

  const zone = context === 'interactive' ? STAFF_ZONE : 'UTC';
  const w = wallClock(zone, now.getTime());
  const today: CalendarDay = { y: w.y, m: w.m, d: w.d };
  const at = (day: CalendarDay) => startOf(zone, day).toISOString();

  switch (token) {
    case '{{today}}':
      return at(today);
    case '{{tomorrow}}':
      return at(addDays(today, 1));
    case '{{yesterday}}':
      return at(addDays(today, -1));
    case '{{dayAfterTomorrow}}':
      return at(addDays(today, 2));
    case '{{weekStart}}':
      // Monday-based (§5.1): Sunday is the 7th day of the week that began the preceding Monday.
      return at(addDays(today, -(MONDAY_OFFSET[w.weekday] ?? 0)));
    case '{{weekEnd}}':
      return at(addDays(today, 7 - (MONDAY_OFFSET[w.weekday] ?? 0)));
    case '{{monthStart}}':
      return at({ y: today.y, m: today.m, d: 1 });
    case '{{monthEnd}}':
      return at(addMonths(today, 1));
    case '{{quarterStart}}':
      return at({ y: today.y, m: Math.floor((today.m - 1) / 3) * 3 + 1, d: 1 });
    case '{{quarterEnd}}':
      return at(addMonths({ y: today.y, m: Math.floor((today.m - 1) / 3) * 3 + 1, d: 1 }, 3));
    case '{{yearStart}}':
      return at({ y: today.y, m: 1, d: 1 });
    case '{{yearEnd}}':
      return at({ y: today.y + 1, m: 1, d: 1 });
    default:
      return null;
  }
}

/** True if any value in the query is a relative-date token. */
export function isDynamicQuery(query: Record<string, string>): boolean {
  return Object.values(query).some((v) => TOKEN_PATTERN.test(v));
}

/**
 * Resolve every `{{token}}` value in a view query to a concrete ISO instant.
 * Static values pass through untouched; unknown tokens are left verbatim (so a typo is visible rather
 * than silently dropping a filter). Returns a NEW object; the input is never mutated.
 *
 * @param context `interactive` (default — staff grids, America/New_York) or `saved` (UTC).
 */
export function resolveViewQuery(
  query: Record<string, string>,
  now: Date = new Date(),
  context: TokenContext = 'interactive'
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(query)) {
    if (TOKEN_PATTERN.test(v)) {
      const resolved = resolveToken(v, now, context);
      out[k] = resolved ?? v;
    } else {
      out[k] = v;
    }
  }
  return out;
}
